import api from './api';

export type GatewayId = 'razorpay' | 'paytm' | 'phonepe' | 'cashfree';

export interface PaymentGatewaysResponse {
  currency: string;
  gateways: Array<{ id: GatewayId; mode: 'test' | 'live' }>;
}

export interface InitiatePaymentRequest {
  orderType?: 'plan' | 'top_up';
  planId?: string;
  topUpPackId?: string;
  couponCode?: string;
}

export const fetchPaymentGateways = async (): Promise<PaymentGatewaysResponse> => {
  const response = await api.get('/api/public/payment-gateways');
  return response.data;
};

export const initiatePayment = async (
  request: string | InitiatePaymentRequest,
  gateway: GatewayId
) => {
  const payload = typeof request === 'string'
    ? { planId: request, orderType: 'plan' }
    : request;
  const response = await api.post('/api/payments/initiate', { ...payload, gateway });
  return response.data;
};

export const verifyPayment = async (payload: Record<string, any>) => {
  const response = await api.post('/api/payments/verify', payload);
  return response.data;
};

declare global {
  interface Window {
    Cashfree?: any;
  }
}

const loadCashfreeSdk = () => new Promise<boolean>((resolve) => {
  if (window.Cashfree) {
    resolve(true);
    return;
  }
  const script = document.createElement('script');
  script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

/**
 * Open the Cashfree hosted checkout for an initiated order. Redirects the
 * browser; on completion Cashfree returns the user to
 * /payment/return?gateway=cashfree&paymentOrderId=... which triggers /verify.
 */
export const openCashfreeCheckout = async (init: {
  paymentSessionId: string;
  cfMode?: 'sandbox' | 'production';
  paymentOrderId: string;
}): Promise<void> => {
  const loaded = await loadCashfreeSdk();
  if (!loaded || !window.Cashfree) {
    throw new Error('Unable to load Cashfree checkout.');
  }
  localStorage.setItem('paymentReturn', JSON.stringify({
    gateway: 'cashfree',
    paymentOrderId: init.paymentOrderId,
  }));
  const cashfree = window.Cashfree({ mode: init.cfMode === 'production' ? 'production' : 'sandbox' });
  await cashfree.checkout({
    paymentSessionId: init.paymentSessionId,
    redirectTarget: '_self',
  });
};

/**
 * Attempt to initiate payment with the given gateway. If it fails (network or
 * server error), automatically retry with each fallback gateway in order.
 *
 * Returns { data, usedGateway } so the caller knows which gateway succeeded.
 */
export const initiateWithFallback = async (
  request: string | InitiatePaymentRequest,
  preferredGateway: GatewayId,
  availableGateways: GatewayId[]
): Promise<{ data: any; usedGateway: GatewayId }> => {
  // Build ordered list: preferred first, then others in config order
  const order: GatewayId[] = [
    preferredGateway,
    ...availableGateways.filter((g) => g !== preferredGateway),
  ];

  let lastError: Error = new Error('No payment gateways available');

  for (const gateway of order) {
    try {
      const data = await initiatePayment(request, gateway);
      return { data, usedGateway: gateway };
    } catch (err: any) {
      console.warn(`Gateway ${gateway} failed:`, err?.response?.data?.message || err.message);
      lastError = err;
      // Only fall through on server-side / network errors (not 400 validation errors)
      const status = err?.response?.status;
      if (status && status < 500 && status !== 0) {
        // Client error — no point retrying other gateways with same input
        throw err;
      }
    }
  }

  throw lastError;
};
